import React, {
  useState,
  useContext,
  createContext,
  useRef,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { showErrorMessages } from "../../../services/utilityFunctions.js";
import {
  ChevronRight,
  ChevronDown,
  Plus,
  FileText,
  Hash,
  Type,
  List,
  Calculator,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Image,
  Link,
  Sigma,
  Check,
  X,
  Trash2,
  Settings,
  AlertTriangle,
} from "lucide-react";
// Import MathLive from local package
import "mathlive";
// import "mathlive/dist/mathlive-fonts.css";
import {
  initialExam,
  ExamContext,
  useExamContext,
  setInitialExamData,
  // ✅ NEW: Import metadata management functions
  updateMetadataForSection,
  updateMetadataForQuestion,
  removeMetadataForSection,
  removeMetadataForQuestion,
} from "./utility.js";
import TopToolbar from "./TopToolbar";
import TreeSidebar from "./TreeSideBar";
import Canvas from "./canvas";
import { useParams } from "react-router-dom";
import { getExaminationTemplateDetails } from "../../../services/Users-services.js";

const QuestionPaperBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isEditingQuestion, setIsEditingQuestion] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const containerRef = useRef(null);
  const [error, setError] = useState(null);

  // ✅ FIXED: Simplified updateExam function - no automatic metadata sync
  const updateExam = (updater) => {
    setExam((prevExam) => {
      if (typeof updater === "function") {
        const newExam = updater(prevExam);
        return newExam;
      } else {
        return { ...updater };
      }
    });
  };
  // ✅ FIXED: Simplified syncMetadataWithExam to avoid unnecessary re-renders
  const syncMetadataWithExam = (examData) => {
    if (!examData.richTextMetadata) {
      examData.richTextMetadata = {};
    }

    // Only sync when structure changes, not on every content update
    const currentSectionIds = new Set();

    examData.pages.forEach((page) => {
      page.sections.forEach((section) => {
        currentSectionIds.add(section.id);

        // Initialize section metadata if it doesn't exist
        if (!examData.richTextMetadata[section.id]) {
          examData.richTextMetadata[section.id] = {
            instructions: section.instructions || null,
            questions: section.questions.map((question) => ({
              questionId: question.id,
              configuration: question.richContent,
            })),
          };
        }
      });
    });

    // Remove metadata for sections that no longer exist
    Object.keys(examData.richTextMetadata).forEach((sectionId) => {
      if (!currentSectionIds.has(sectionId)) {
        delete examData.richTextMetadata[sectionId];
      }
    });
  };

  const selectNode = (type, id) => {
    setSelectedNode({ type, id });
  };

  const clearSelection = () => {
    setSelectedNode(null);
  };

  const setEditingMode = (isEditing) => {
    setIsEditingQuestion(isEditing);
  };

  const fetchExamDetails = async () => {
    try {
      setLoading(true);
      const response = await getExaminationTemplateDetails(id);

      setError(null);
      if (response.status == "success") {
        const examData = setInitialExamData(response.data);

        // ✅ NEW: Ensure metadata structure is properly initialized
        if (!examData.richTextMetadata) {
          examData.richTextMetadata = {};
        }
        console.log("Exam data fetched:", examData);

        setExam(examData);
      } else {
        showErrorMessages({
          data: response,
          navigate,
        });
        setError(response.message)
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching exam details:", error);
      setError("Failed to load exam details. Please try again later.");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExamDetails();
  }, [id]);

  // Global click handler for deselection
  // In QuestionPaperBuilder.js, update the useEffect with global click handler
  useEffect(() => {
    const handleGlobalClick = (event) => {
      console.log("Global click detected on:", event.target);

      // ✅ CRITICAL: Enhanced MathLive element detection
      const isMathLiveElement = event.target.closest(
        ".ML__keyboard, .ML__keycap, .ML__virtual-keyboard-toggle, .ML__keyboard-layer, .ML__keyboard-row, math-field, .mathlive-container, .math-field-wrapper, .enhanced-math-field, .enhanced-math-preview, .ML__backdrop, .ML__keyboard-backdrop"
      );

      // ✅ CRITICAL: Check if virtual keyboard is visible and being interacted with
      const isVirtualKeyboardVisible = document.querySelector(".ML__keyboard");
      const isVirtualKeyboardInteraction =
        event.target.classList.contains("ML__keycap") ||
        event.target.closest(".ML__keycap") ||
        event.target.closest(".ML__keyboard");

      // ✅ CRITICAL: Check if any math field is currently focused
      const activeMathField = document.querySelector("math-field:focus");
      const isMathFieldFocused =
        document.activeElement &&
        document.activeElement.tagName === "MATH-FIELD";

      console.log("Click analysis:", {
        isMathLiveElement: !!isMathLiveElement,
        isVirtualKeyboardVisible: !!isVirtualKeyboardVisible,
        isVirtualKeyboardInteraction,
        activeMathField: !!activeMathField,
        isMathFieldFocused,
        activeElement: document.activeElement?.tagName,
      });

      // ✅ CRITICAL: Don't close math editor if interacting with MathLive components
      if (
        isMathLiveElement ||
        (isVirtualKeyboardVisible && isVirtualKeyboardInteraction) ||
        activeMathField ||
        isMathFieldFocused
      ) {
        console.log("MathLive interaction detected - preventing editor close");
        return; // Don't close editing mode
      }

      // Check if click is on other interactive elements
      const isClickOnInteractiveElement = event.target.closest(
        ".tree-node, .question-item, .section, .toolbar-btn, .inline-question-editor, .enhanced-rich-text-editor, .modal-overlay, .templates-panel, .math-editor-panel, .inline-editable, .inline-editor-container, .section-title-editor" // ✅ NEW: Added .section-title-editor
      );

      // If click is outside interactive elements, clear selections and exit edit modes
      if (!isClickOnInteractiveElement) {
        console.log("Click outside interactive elements - clearing selections");
        if (selectedNode) {
          clearSelection();
        }
        if (isEditingQuestion) {
          setEditingMode(false);
        }
      }
    };

    // Add event listener with capture phase to catch events early
    document.addEventListener("click", handleGlobalClick, true);

    // Cleanup
    return () => {
      document.removeEventListener("click", handleGlobalClick, true);
    };
  }, [selectedNode, isEditingQuestion]);

  // ✅ NEW: Enhanced context value with metadata management
  const contextValue = {
    exam,
    selectedNode,
    isEditingQuestion,
    updateExam,
    selectNode,
    clearSelection,
    setEditingMode,
    fetchExamDetails,

    // ✅ NEW: Metadata management functions
    updateSectionMetadata: (sectionId, instructions) => {
      // ✅ FIXED: Update metadata without triggering re-render
      setExam((prevExam) => ({
        ...prevExam,
        richTextMetadata: updateMetadataForSection(
          prevExam.richTextMetadata || {},
          sectionId,
          instructions
        ),
      }));
    },

    updateQuestionMetadata: (sectionId, questionId, configuration) => {
      // ✅ FIXED: Update metadata without triggering re-render
      setExam((prevExam) => ({
        ...prevExam,
        richTextMetadata: updateMetadataForQuestion(
          prevExam.richTextMetadata || {},
          sectionId,
          questionId,
          configuration
        ),
      }));
    },

    removeSectionMetadata: (sectionId) => {
      updateExam((exam) => ({
        ...exam,
        richTextMetadata: removeMetadataForSection(
          exam.richTextMetadata || {},
          sectionId
        ),
      }));
    },

    removeQuestionMetadata: (sectionId, questionId) => {
      updateExam((exam) => ({
        ...exam,
        richTextMetadata: removeMetadataForQuestion(
          exam.richTextMetadata || {},
          sectionId,
          questionId
        ),
      }));
    },

    // ✅ NEW: Helper to get metadata
    getSectionMetadata: (sectionId) => {
      return exam?.richTextMetadata?.[sectionId]?.instructions || null;
    },

    getQuestionMetadata: (sectionId, questionId) => {
      const sectionMeta = exam?.richTextMetadata?.[sectionId];
      if (!sectionMeta) return null;

      const questionMeta = sectionMeta.questions.find(
        (q) => q.questionId === questionId
      );
      return questionMeta?.configuration || null;
    },
  };

  useEffect(() => {
    const handleGlobalClick = (event) => {
      // ✅ IMPROVED: Better MathLive element detection
      const isMathLiveElement = event.target.closest(
        ".ML__keyboard, .ML__keycap, .ML__virtual-keyboard-toggle, .ML__keyboard-layer, .ML__keyboard-row, math-field, .mathlive-container, .math-field-wrapper, .enhanced-math-field, .enhanced-math-preview, .ML__backdrop"
      );

      // ✅ IMPROVED: Check if any MathLive virtual keyboard is currently visible
      const isVirtualKeyboardVisible = document.querySelector(".ML__keyboard");

      // ✅ IMPROVED: Check if user is actively typing in a math field
      const activeMathField = document.querySelector("math-field:focus");

      // ✅ IMPROVED: If clicking on MathLive elements, virtual keyboard is visible, or math field is active, don't close editing
      if (isMathLiveElement || isVirtualKeyboardVisible || activeMathField) {
        return; // Don't close editing mode
      }

      // Check if click is on other interactive elements
      const isClickOnInteractiveElement = event.target.closest(
        ".tree-node, .question-item, .section, .toolbar-btn, .inline-question-editor, .enhanced-rich-text-editor, .modal-overlay, .templates-panel, .math-editor-panel, .inline-editable, .inline-editor-container"
      );

      // If click is outside interactive elements, clear selections and exit edit modes
      if (!isClickOnInteractiveElement) {
        if (selectedNode) {
          clearSelection();
        }
        if (isEditingQuestion) {
          setEditingMode(false);
        }
      }
    };

    // Add event listener to document
    document.addEventListener("click", handleGlobalClick, true);

    // Cleanup
    return () => {
      document.removeEventListener("click", handleGlobalClick, true);
    };
  }, [selectedNode, isEditingQuestion]);

  const LoadingOverlay = () => (
    <div className="loading-overlay">
      <div className="loading-content">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading exam details...</p>
      </div>
    </div>
  );

  if(error){
    return <div>
      {error}
    </div>
  }

  return (
    <ExamContext.Provider value={contextValue}>
      {loading && <LoadingOverlay />}

      {error != null ? (
        <div>{error}</div>
      ) : loading ? (
        <></>
      ) : (
        <div className="question-paper-builder" ref={containerRef}>
          <TopToolbar
            setLoading={setLoading}
            fetchExamDetails={fetchExamDetails}
          />
          <div className="main-content">
            <TreeSidebar
              collapsed={sidebarCollapsed}
              onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
            />
            <Canvas />
          </div>
        </div>
      )}

      <style jsx>{`
        .loading-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          backdrop-filter: blur(4px);
          animation: fadeIn 0.3s ease-out;
        }

        .loading-content {
          background: rgba(255, 255, 255, 0.95);
          border-radius: 16px;
          padding: 3rem 2.5rem;
          text-align: center;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
            0 10px 10px -5px rgba(0, 0, 0, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(20px);
          min-width: 280px;
          animation: slideInScale 0.4s ease-out;
        }

        .loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e2e8f0;
          border-top: 4px solid #3b82f6;
          border-radius: 50%;
          margin: 0 auto 1.5rem;
          animation: spin 1s linear infinite;
        }

        .loading-text {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 500;
          color: #374151;
          letter-spacing: -0.025em;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideInScale {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        /* Ensure loading overlay is above everything */
        .loading-overlay {
          pointer-events: auto;
          user-select: none;
        }

        /* Prevent interaction with background content during loading */
        .loading-overlay ~ * {
          pointer-events: none;
        }

        /* Scrollbar styling for webkit browsers */
        ::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }

        ::-webkit-scrollbar-track {
          background: #f1f5f9;
        }

        ::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        /* Enhanced MathLive Math Field Global Styles - Increased Width */
        :global(.ML__keyboard) {
          pointer-events: auto !important;
          user-select: none !important;
          z-index: 2000 !important; /* Higher z-index to ensure it's on top */
        }

        :global(.ML__keyboard .ML__keycap) {
          pointer-events: auto !important;
          user-select: none !important;
          cursor: pointer !important;
        }

        :global(.ML__keycap:hover) {
          background: #3b82f6 !important;
          color: white !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 4px 16px rgba(59, 130, 246, 0.4) !important;
          border-color: #2563eb !important;
        }

        :global(.ML__keycap:active) {
          transform: translateY(0) !important;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3) !important;
        }

        /* ✅ CRITICAL: Ensure math field stays focused during virtual keyboard interaction */
        :global(math-field:focus) {
          outline: 2px solid #3b82f6 !important;
          outline-offset: 2px !important;
          border-color: #3b82f6 !important;
        }

        /* ✅ CRITICAL: Virtual keyboard backdrop handling */
        :global(.ML__keyboard-backdrop) {
          pointer-events: none !important;
        }

        /* ✅ CRITICAL: Prevent virtual keyboard from being affected by global styles */
        :global(.ML__virtual-keyboard-toggle) {
          background: #3b82f6 !important;
          color: white !important;
          border-radius: 8px !important;
          padding: 8px 12px !important;
          font-size: 16px !important;
          border: none !important;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3) !important;
          transition: all 0.2s ease !important;
          pointer-events: auto !important;
          z-index: 1500 !important;
        }

        :global(.ML__virtual-keyboard-toggle:hover) {
          background: #2563eb !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4) !important;
        }
        :global(math-field) {
          min-width: 1400px !important;
          max-width: 100% !important;
          width: 100% !important;
          min-height: 140px !important;
          border-radius: 12px !important;
          border: 2px solid #d1d5db !important;
          padding: 28px 32px !important;
          font-size: 22px !important;
          line-height: 1.6 !important;
          background: #ffffff !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
          transition: all 0.3s ease !important;
          display: block !important;
          box-sizing: border-box !important;
        }

        :global(math-field:focus-within) {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1),
            0 4px 16px rgba(0, 0, 0, 0.12) !important;
        }

        /* Target the enhanced-math-field class specifically */
        :global(.enhanced-math-field) {
          min-width: 1400px !important;
          max-width: 100% !important;
          width: 100% !important;
          min-height: 140px !important;
          display: block !important;
          box-sizing: border-box !important;
        }

        /* Target MathLive's internal elements for width control */
        :global(math-field .ML__fieldcontainer) {
          min-width: 1400px !important;
          width: 100% !important;
        }

        :global(math-field .ML__content) {
          min-width: 1400px !important;
          width: 100% !important;
        }

        :global(math-field .ML__textarea) {
          min-width: 1400px !important;
          width: 100% !important;
        }

        /* Enhanced Virtual Keyboard Global Styles */
        :global(.ML__keyboard) {
          --keycap-width: 60px !important;
          --keycap-height: 50px !important;
          --keycap-font-size: 18px !important;
          --keyboard-height: auto !important;
          border-radius: 12px !important;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15) !important;
          border: 2px solid #e2e8f0 !important;
          background: #ffffff !important;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            sans-serif !important;
          z-index: 1000 !important;
        }

        :global(.ML__keyboard .ML__keycap) {
          border-radius: 8px !important;
          font-weight: 500 !important;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
          background: #f8fafc !important;
          border: 1px solid #e2e8f0 !important;
          color: #374151 !important;
          font-size: 16px !important;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
        }

        :global(.ML__keyboard .ML__keycap:hover) {
          background: #3b82f6 !important;
          color: white !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 4px 16px rgba(59, 130, 246, 0.4) !important;
          border-color: #2563eb !important;
        }

        :global(.ML__keyboard .ML__keycap:active) {
          transform: translateY(0) !important;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3) !important;
        }

        /* Special keycap types */
        :global(.ML__keyboard .ML__keycap.action) {
          background: #10b981 !important;
          color: white !important;
          font-weight: 600 !important;
        }

        :global(.ML__keyboard .ML__keycap.action:hover) {
          background: #059669 !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 4px 16px rgba(16, 185, 129, 0.4) !important;
        }

        :global(.ML__keyboard .ML__keycap.modifier) {
          background: #6b7280 !important;
          color: white !important;
          font-weight: 600 !important;
        }

        :global(.ML__keyboard .ML__keycap.modifier:hover) {
          background: #4b5563 !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 4px 16px rgba(107, 114, 128, 0.4) !important;
        }

        :global(.ML__keyboard .ML__keycap.delete) {
          background: #ef4444 !important;
          color: white !important;
          font-weight: 600 !important;
        }

        :global(.ML__keyboard .ML__keycap.delete:hover) {
          background: #dc2626 !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 4px 16px rgba(239, 68, 68, 0.4) !important;
        }

        /* Virtual keyboard layout improvements */
        :global(.ML__keyboard .ML__keyboard-layer) {
          padding: 16px !important;
          gap: 8px !important;
        }

        :global(.ML__keyboard .ML__keyboard-row) {
          gap: 6px !important;
          margin-bottom: 6px !important;
        }

        /* Enhanced math field styling globally with increased width */
        :global(math-field) {
          border-radius: 12px !important;
          border: 2px solid #d1d5db !important;
          padding: 24px 28px !important;
          font-size: 20px !important;
          line-height: 1.6 !important;
          background: #ffffff !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
          transition: all 0.3s ease !important;
          min-width: 1200px !important;
        }
        :global(.ML__keyboard) {
          pointer-events: auto !important;
          user-select: none !important;
        }

        :global(.ML__keyboard .ML__keycap) {
          pointer-events: auto !important;
          user-select: none !important;
        }

        /* ✅ IMPROVED: Ensure math field stays focused during virtual keyboard interaction */
        :global(math-field:focus) {
          outline: 2px solid #3b82f6 !important;
          outline-offset: 2px !important;
        }

        :global(math-field:focus-within) {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1),
            0 4px 16px rgba(0, 0, 0, 0.12) !important;
        }

        /* Virtual keyboard toggle button styling */
        :global(.ML__virtual-keyboard-toggle) {
          background: #3b82f6 !important;
          color: white !important;
          border-radius: 8px !important;
          padding: 8px 12px !important;
          font-size: 16px !important;
          border: none !important;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3) !important;
          transition: all 0.2s ease !important;
        }

        :global(.ML__virtual-keyboard-toggle:hover) {
          background: #2563eb !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4) !important;
        }

        /* Custom focus ring for better accessibility */
        .focus-ring:focus {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }

        .question-paper-builder {
          display: flex;
          flex-direction: column;
          height: 100vh;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            "Helvetica Neue", Arial, sans-serif;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          color: #1e293b;
          overflow: hidden;
          min-width: 1200px;
        }

        .main-content {
          display: flex;
          flex: 1;
          overflow: hidden;
          position: relative;
          max-width: 100vw;
        }

        /* Modal Styles - Professional Hope UI */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          backdrop-filter: blur(4px);
          animation: fadeIn 0.2s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .modal-content {
          background: #ffffff;
          border-radius: 8px;
          padding: 0;
          min-width: 480px;
          max-width: 560px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
            0 10px 10px -5px rgba(0, 0, 0, 0.04);
          border: 1px solid #e2e8f0;
          animation: slideIn 0.3s ease-out;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-16px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem 2rem;
          border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
          border-radius: 8px 8px 0 0;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 1.125rem;
          font-weight: 600;
          color: #1e293b;
          letter-spacing: -0.025em;
        }

        .modal-close-btn {
          background: none;
          border: none;
          color: #64748b;
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 6px;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .modal-close-btn:hover {
          background: #f1f5f9;
          color: #374151;
        }

        .modal-body {
          padding: 2rem;
        }

        .setting-group {
          margin-bottom: 1.5rem;
        }

        .setting-group:last-child {
          margin-bottom: 0;
        }

        .setting-group label {
          display: block;
          margin-bottom: 0.75rem;
          font-weight: 500;
          color: #374151;
          font-size: 0.9rem;
        }

        .setting-select,
        .setting-input {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.9rem;
          transition: all 0.2s ease;
          background: #ffffff;
          color: #1e293b;
        }

        .setting-select:focus,
        .setting-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .setting-info {
          padding: 1.5rem;
          background: #f8fafc;
          border-radius: 6px;
          margin-top: 1.5rem;
          border: 1px solid #e2e8f0;
        }

        .setting-info p {
          margin: 0.5rem 0;
          font-size: 0.875rem;
          color: #64748b;
          line-height: 1.6;
        }

        .setting-info p:first-child {
          margin-top: 0;
        }

        .setting-info p:last-child {
          margin-bottom: 0;
        }

        .modal-footer {
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
          padding: 1.5rem 2rem;
          border-top: 1px solid #e2e8f0;
          background: #f8fafc;
          border-radius: 0 0 8px 8px;
        }

        .modal-save-btn {
          padding: 0.75rem 1.5rem;
          background: #10b981;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 500;
          font-size: 0.9rem;
          transition: all 0.2s ease;
          box-shadow: 0 1px 3px rgba(16, 185, 129, 0.2);
        }

        .modal-save-btn:hover {
          background: #059669;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .modal-cancel-btn {
          padding: 0.75rem 1.5rem;
          background: #ffffff;
          color: #374151;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          font-size: 0.9rem;
          transition: all 0.2s ease;
        }

        .modal-cancel-btn:hover {
          background: #f9fafb;
          border-color: #9ca3af;
        }

        /* Print Styles */
        @media print {
          .tree-sidebar,
          .top-toolbar {
            display: none !important;
          }
          .canvas-container {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }
          .canvas {
            box-shadow: none !important;
            border-radius: 0 !important;
            margin: 0 !important;
            border: none !important;
          }
          .page {
            page-break-after: always;
            box-shadow: none !important;
            border: none !important;
            margin: 0 !important;
          }
          .page:last-child {
            page-break-after: avoid;
          }
          .page-header {
            display: block !important;
          }
          .edit-icon,
          .inline-editable:hover .edit-icon {
            display: none !important;
          }
        }

        /* Responsive Design */
        @media (max-width: 1920px) {
          .main-content {
            flex-direction: row;
          }

          .question-paper-builder {
            font-size: 1rem;
            min-width: 1100px;
          }

          .tree-sidebar {
            width: 400px;
            min-width: 400px;
          }

          .modal-content {
            min-width: 520px;
            max-width: 700px;
          }
        }

        @media (max-width: 1600px) {
          .main-content {
            flex-direction: row;
          }

          .question-paper-builder {
            font-size: 0.95rem;
            min-width: 1000px;
          }

          .tree-sidebar {
            width: 380px;
            min-width: 380px;
          }

          .modal-content {
            min-width: 480px;
            max-width: 650px;
          }
        }

        @media (max-width: 1440px) {
          .question-paper-builder {
            font-size: 0.9rem;
            min-width: 900px;
          }

          .tree-sidebar {
            width: 360px;
            min-width: 360px;
          }
        }

        @media (max-width: 1024px) {
          .main-content {
            flex-direction: column;
          }

          .question-paper-builder {
            font-size: 0.9rem;
            min-width: 100%;
          }

          .tree-sidebar {
            width: 100%;
            min-width: 100%;
            max-height: 300px;
          }

          .modal-content {
            min-width: 90vw;
            max-width: 95vw;
            margin: 1rem;
          }

          .modal-header,
          .modal-body,
          .modal-footer {
            padding: 1rem 1.5rem;
          }
        }

        @media (max-width: 768px) {
          .question-paper-builder {
            font-size: 0.875rem;
            min-width: 100%;
          }

          .tree-sidebar {
            max-height: 250px;
          }

          .modal-content {
            min-width: 95vw;
            max-width: 98vw;
            margin: 0.5rem;
          }

          .modal-header,
          .modal-body,
          .modal-footer {
            padding: 1rem;
          }

          .modal-header h3 {
            font-size: 1rem;
          }

          .setting-select,
          .setting-input {
            padding: 0.625rem 0.875rem;
            font-size: 0.875rem;
          }

          .modal-save-btn,
          .modal-cancel-btn {
            padding: 0.625rem 1.25rem;
            font-size: 0.875rem;
          }
        }

        /* Focus and Accessibility */
        .modal-close-btn:focus,
        .setting-select:focus,
        .setting-input:focus,
        .modal-save-btn:focus,
        .modal-cancel-btn:focus {
          outline: 2px solid transparent;
          outline-offset: 2px;
        }

        /* Smooth transitions for interactive elements */
        * {
          transition-property: color, background-color, border-color, box-shadow,
            transform;
          transition-duration: 0.2s;
          transition-timing-function: ease-out;
        }

        /* Improved text rendering */
        .question-paper-builder {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility;
        }

        /* Selection colors */
        ::selection {
          background: rgba(59, 130, 246, 0.2);
          color: #1e293b;
        }

        /* Scrollbar styling for webkit browsers */
        ::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }

        ::-webkit-scrollbar-track {
          background: #f1f5f9;
        }

        ::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        /* Custom focus ring for better accessibility */
        .focus-ring:focus {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }
      `}</style>
    </ExamContext.Provider>
  );
};

export default QuestionPaperBuilder;
